import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { SectionCamera, ProductionGroup, ProductionGroupBarnResponse, BackfillSuggestionsResponse } from '@/types';
import ApiClient from '@/plugins/store/actions/api/ApiClient';
import type { PigTallyDay } from '@/types/SectionLiveKpi';

export type ProductionGroupTimelineRequest = {
    sectionId: number | null;
    barnId: number | null;
    from: string;
    to: string;
};

// Which Section to resolve cameras for, and the date to resolve them as of. The date is the one
// currently entered in the form, not a persisted Production Group date, so the mounted camera set can
// be previewed for a start date before anything is saved.
export type SectionCamerasRequest = {
    sectionId: number;
    date: string;
};

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
};

export const queryKeys = {
    getProductionGroupsRoot: ['production-groups'],
    getBarnProductionGroups: (barnIds: Ref<number[]>, selectedPeriod: Ref<string>) => [...queryKeys.getProductionGroupsRoot, { barnIds, selectedPeriod }],
    getProductionGroupTimeline: (request: Ref<ProductionGroupTimelineRequest | null>) => [...queryKeys.getProductionGroupsRoot, 'timeline', request],
    getSectionCameras: (request: Ref<SectionCamerasRequest | null>) => [...queryKeys.getProductionGroupsRoot, 'cameras', request],
    getBackfillSuggestions: (sectionId: Ref<number | null>, fromDate: Ref<string | null>) => [...queryKeys.getProductionGroupsRoot, 'backfill-suggestions', sectionId, fromDate],
    getBackfillPigTallySeries: (sectionId: Ref<number | null>, range: Ref<{ from: string; to: string } | null>) => [
        ...queryKeys.getProductionGroupsRoot,
        'backfill-pig-tally',
        sectionId,
        range,
    ],
};

export function useBarnProductionGroups({ barnIds, selectedPeriod }: { barnIds: Ref<number[]>; selectedPeriod: Ref<string> }, options: UseQueryOptions<ProductionGroupBarnResponse> = {}) {
    return useQuery<ProductionGroupBarnResponse>({
        queryKey: queryKeys.getBarnProductionGroups(barnIds, selectedPeriod),
        queryFn: () =>
            store.dispatch(ActionType.GetProductionGroups, {
                barnIds: barnIds.value,
                period: selectedPeriod.value,
            }),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

// A Production Group setup modal needs only its own Section's history, but the existing Barn endpoint
// is the authoritative windowed read. Keep that transport detail here and return the exact Section
// slice to the component rather than making the component assemble an API response itself.
//
// `skipHandleError` because the modal that consumes this renders its own `isError` state (retry
// button, "the timeline could not be loaded") - without it, ApiClient's central handler would toast a
// generic error and resolve the query with `undefined` instead of rejecting, so the dedicated error UI
// would never fire.
export function useProductionGroupTimeline(request: Ref<ProductionGroupTimelineRequest | null>) {
    return useQuery<ProductionGroup[]>({
        queryKey: queryKeys.getProductionGroupTimeline(request),
        queryFn: async () => {
            const value = request.value;
            if (!value?.sectionId || !value.barnId) return [];

            const barns = (await ApiClient.send('get', `monitor/production-groups/barns?barn_ids=${value.barnId}&from=${value.from}&to=${value.to}`, {
                skipHandleError: true,
            })) as ProductionGroupBarnResponse;
            return barns.flatMap((barn) => barn.production_groups).filter((productionGroup) => productionGroup.location_id === value.sectionId);
        },
        enabled: () => request.value?.sectionId != null && request.value.barnId != null,
        ...DEFAULT_OPTIONS,
    });
}

// Cameras mounted on a Section as of a given date. Read-only: the result is displayed, never
// submitted, so a failure is reported by the central error handler and resolves without data, which
// the caller renders as an empty camera set. That indistinguishability is why the result is not
// cached — a swallowed failure must not keep asserting "no cameras" for a section that has them.
export function useSectionCameras(request: Ref<SectionCamerasRequest | null>) {
    return useQuery<SectionCamera[]>({
        queryKey: queryKeys.getSectionCameras(request),
        queryFn: async () => {
            const value = request.value;
            if (!value) return [];

            return (await ApiClient.send('get', `monitor/production-groups/cameras?location_id=${value.sectionId}&date=${value.date}`, {})) ?? [];
        },
        enabled: () => request.value !== null,
        ...DEFAULT_OPTIONS,
        staleTime: 0,
    });
}

// The backfill wizard's "detect" step for one Section - on demand (sectionId != null), re-run
// whenever the farmer overrides the from-date. `fromDate` null means "use the backend's own
// default" (the Section's earliest sensor mount), not "no request".
// `skipHandleError` for the same reason as useProductionGroupTimeline/useBackfillPigTallySeries
// below - the wizard renders its own retry-able error state for this read, which a swallowed-to-toast
// failure would skip (ApiClient would otherwise resolve with no data and no way to distinguish that
// from a genuinely empty result).
export function useBackfillSuggestions(sectionId: Ref<number | null>, fromDate: Ref<string | null>) {
    return useQuery<BackfillSuggestionsResponse>({
        queryKey: queryKeys.getBackfillSuggestions(sectionId, fromDate),
        queryFn: () => {
            const overrideParam = fromDate.value ? `?from=${fromDate.value}` : '';
            return ApiClient.send('get', `monitor/production-groups/${sectionId.value}/backfill-suggestions${overrideParam}`, { skipHandleError: true });
        },
        enabled: () => sectionId.value !== null,
        staleTime: 0,
        gcTime: 1000 * 60 * 5,
    });
}

// The backfill wizard's review chart, for an explicit range that routinely spans years - deliberately
// NOT active-groups' usePigTallySeries (that one's backend caps lookback at six months for its own
// KPI-detail caller). Reads through the backfill business instead, the same calculator call the
// suggestion detection itself makes, so the chart matches exactly what was detected.
//
// `skipHandleError` for the same reason as useProductionGroupTimeline above - the Manage Group modal
// renders its own retry-able error state for this read, which a swallowed-to-toast failure would skip.
export function useBackfillPigTallySeries(sectionId: Ref<number | null>, range: Ref<{ from: string; to: string } | null>) {
    return useQuery<PigTallyDay[]>({
        queryKey: queryKeys.getBackfillPigTallySeries(sectionId, range),
        queryFn: () => {
            const r = range.value!;
            return ApiClient.send('get', `monitor/production-groups/${sectionId.value}/pig-tally?from=${r.from}&to=${r.to}`, { skipHandleError: true });
        },
        enabled: () => sectionId.value !== null && range.value !== null,
        staleTime: 1000 * 60,
        gcTime: 1000 * 60 * 5,
    });
}
